import React, { FunctionComponent, useEffect, useRef, useState } from "react";

import { ButtonBackgroundColor, FlexGrow, FontWeight, Layout, LocalizationMultiLoader, LocalizationProvider, ModalWindow, SectionPanel, SectionPanelContent, SectionPanelFooter, SectionPanelHeader, TextButton, TextColor } from "@multiweb/reactComponents";
import { Key, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { Deferred, IParticipant } from "@multiweb/types/multiweb";
import { RecordType, RootNodeTypedObject, TypedObject } from "@multiweb/types/react";
import { GridParticipants } from "../GridParticipants";
import { WFParticipantsDialogProps } from "../WFParticipantsDialog";
import { LOC_KEY_CANCEL, LOC_KEY_CHECK_ALL, LOC_KEY_CHOOSE, LOC_KEY_CHOOSE_PARTICIPANTS, LOC_KEY_DISPLAY_USERS_OF_ROLE, LOC_KEY_HIDE_USERS_ROLE, LOC_KEY_UNCHECK_ALL, STATIC_LOC_KEYS } from "./lockeys";

export type WFParticipantsDialogComponentProps = WFParticipantsDialogProps & {
    /** Odložený promise, který po potvrzení výběru navrátí data, nebo při zavření okna se rejectne. */
    readonly dialogCompletedPromise: Deferred<IParticipant[]>;
    /** Zda jsou všechny záznamy zaškrtnuty. */
    readonly allRecordsChecked?: boolean;
    /** Zda se mají defaultně rozbalit uživatelé rolí. */
    readonly chooseFromUsersDefault: boolean;
    /** Zda dataSource obsahuje záznamy. */
    isEmpty: boolean;
    /** Metoda pro zaškrtnutí / odškrtnutí všech řádků. */
    toggleCheckAllRecords: () => void;
    /** Seznam kořenových uzlů */
    rootParticipants?: RootNodeTypedObject;
    /** Všechny záznamy hráčů se všemi informacemi, pozor, uživatelé rolí jsou zanoření */
    participantRecords?: TypedObject<RecordType>;
    /** Mapa výběru hráčů (seznam dvojic id + true/false podle toho, zda je hráč vybrán) */
    participantsSelection?: TypedObject<boolean>;
};

/** Idčko pro header, propojí se s ModalWindow pro přístupnost. */
const WF_PARTICIPANTS_HEADER_ID = "WFParticipantsHeader";

export const WFParticipantsDialogComponent: FunctionComponent<WFParticipantsDialogComponentProps> = ({
    dialogCompletedPromise,
    allRecordsChecked,
    toggleCheckAllRecords,
    dataSourceKey,
    rootParticipants,
    participantRecords,
    participantsSelection,
    isEmpty,
    chooseFromUsersDefault = false,
}) => {
    const [expandRoleUsers, setExpandRoleUsers] = useState(chooseFromUsersDefault);
    const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);

    const sectionPanelRef = useRef<HTMLDivElement>(null);
    const firstButtonRef = useRef<HTMLButtonElement>(null);

    const getFirstFocusableTabIndexedElement = () => sectionPanelRef.current?.querySelector("[tabindex='0']");

    useEffect(() => {
        sectionPanelRef.current?.addEventListener("blur", (e: FocusEvent) => {
            // Pokud focus přechází ze SectionPanelu na vnitřní element a nebyl nalezen první záznam v seznamu hráčů, tak se v seznamu vyvolá scroll a tím se první záznam zaměří.
            if (sectionPanelRef.current?.contains(e.relatedTarget as Node) && !getFirstFocusableTabIndexedElement()) {
                setScrollToIndex(0);
            }
        });
    }, []);

    /** Zaměří první element ze seznamu hráčů. Pokud úspěšně, tak vrací `true`. */
    const focusParticipantsSection = (): boolean | void => {
        const firstTabIndexedElement = getFirstFocusableTabIndexedElement();

        if (firstTabIndexedElement) {
            (firstTabIndexedElement as HTMLElement).focus();
            return true;
        }
    };

    const firstButtonKeyDownHandler = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === Key.Tab && e.shiftKey) {
            e.preventDefault();
            if (!focusParticipantsSection()) {
                setScrollToIndex(0);
            }
        }
    };

    const onScrollParticipantsHandler = () => {
        if (scrollToIndex === 0) {
            focusParticipantsSection();
            setScrollToIndex(undefined);
        }
    };

    const focusDialog = () => {
        sectionPanelRef.current?.focus();
    };

    const cancelActionHandler = () => {
        dialogCompletedPromise.reject();
    };

    const confirmDialog = () => {
        const allParticipants = Object.values(participantRecords || {}); // Získáme všechny hráče.

        dialogCompletedPromise.resolve(
            allParticipants
                .concat( // Do pole přidáme i zanořené uživatele rolí.
                    allParticipants.flatMap(participant => participant["roleusers"]?.value ?? []),
                )
                .filter(
                    (participant, index, self) => { // Předávat se ale budou jen hráči, kteří splňují 3 podmínky:
                        const id: string = participant["id"].value;
                        return (
                            participantsSelection?.[id] && // Filtrujeme jen vybrané hráče.
                            (!rootParticipants ||
                                Object.entries(rootParticipants).every(([rootParticipantId, rootParticipant]) => // Odstraníme uživatele, kteří jsou v některé z vybraných rolí.
                                    participantsSelection[rootParticipantId] ? !rootParticipant.children.includes(id.toString()) : true)
                            ) && index === self.findIndex((obj) => ( // Hráč nesmí být duplicitní - elegantní mazání duplicit https://stackoverflow.com/a/36744732
                                obj.id.value === participant.id.value
                            ))
                        );
                    })
                .map(
                    participant => ({
                        id: participant["id"].value,
                        name: participant["name"].value,
                        participantType: participant["participanttype"].value,
                    }),
                ),
        );
    };

    const toggleAllUsersOfRoleActionHandler = () => {
        setExpandRoleUsers(previousState => !previousState);
    };

    return (
        <ModalWindow
            defaultWidth={400}
            defaultHeight={500}
            minHeight={400}
            minWidth={400}
            isDisplayed
            draggable
            resizable
            labelId={WF_PARTICIPANTS_HEADER_ID}
            onCloseChange={() => null}
            onContentFocus={focusDialog}
        >
            <SectionPanel onCancelAction={cancelActionHandler} ref={sectionPanelRef} tabIndex={TAB_INDEX_FOCUSABLE_TAB_SEQUENCE}>
                <LocalizationMultiLoader locKeys={STATIC_LOC_KEYS}>
                    <LocalizationProvider locKey={LOC_KEY_CHOOSE_PARTICIPANTS}>
                        {({ text }) => <SectionPanelHeader id={WF_PARTICIPANTS_HEADER_ID} title={text} />}
                    </LocalizationProvider>
                    <SectionPanelContent>
                        <GridParticipants
                            onEnterPress={confirmDialog}
                            onScroll={onScrollParticipantsHandler}
                            {...{ dataSourceKey, expandRoleUsers, chooseFromUsersDefault, scrollToIndex }}
                        />
                    </SectionPanelContent>
                    <SectionPanelFooter>
                        <Layout>
                            <LocalizationProvider locKey={allRecordsChecked ? LOC_KEY_UNCHECK_ALL : LOC_KEY_CHECK_ALL}>
                                {({ text }) =>
                                    <TextButton
                                        onClick={toggleCheckAllRecords}
                                        onKeyDown={firstButtonKeyDownHandler}
                                        text={text}
                                        color={TextColor.blue}
                                        fontWeight={FontWeight.bold}
                                        disabled={isEmpty}
                                        ref={firstButtonRef}
                                        qa={"ToggleCheckAllButton"}
                                    />
                                }
                            </LocalizationProvider>
                            <LocalizationProvider locKey={expandRoleUsers ? LOC_KEY_HIDE_USERS_ROLE : LOC_KEY_DISPLAY_USERS_OF_ROLE}>
                                {({ text }) =>
                                    <TextButton
                                        onClick={toggleAllUsersOfRoleActionHandler}
                                        text={text}
                                        fontWeight={FontWeight.bold}
                                        color={TextColor.blue}
                                        disabled={isEmpty}
                                        qa={"ToggleShowRoleUsersButton"}
                                    />
                                }
                            </LocalizationProvider>
                            <Layout flexGrow={FlexGrow.one} />
                            <LocalizationProvider locKey={LOC_KEY_CANCEL}>
                                {({ text }) =>
                                    <TextButton
                                        onClick={cancelActionHandler}
                                        text={text}
                                        fontWeight={FontWeight.bold}
                                        color={TextColor.blue}
                                        qa={"NoButton"}
                                    />
                                }
                            </LocalizationProvider>
                            <LocalizationProvider locKey={LOC_KEY_CHOOSE}>
                                {({ text }) =>
                                    <TextButton
                                        onClick={confirmDialog}
                                        text={text}
                                        backgroundColor={ButtonBackgroundColor.buttonPrimary}
                                        color={TextColor.textPrimaryInverted}
                                        qa={"OKButton"}
                                        onKeyDown={(e) => e.key === Key.Tab && !e.shiftKey ? e.preventDefault() : undefined} // Zablokování možnosti vyjet Tabulátorem z dialogu.
                                    />
                                }
                            </LocalizationProvider>
                        </Layout>
                    </SectionPanelFooter>
                </LocalizationMultiLoader>
            </SectionPanel>
        </ModalWindow>
    );
};
